import moment from 'moment';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Card, CardBody, CardHeader, CardSubtitle, CardTitle } from 'reactstrap';
import { getTrustAccountDetail } from '../../../../api/trust';
import { Loader } from '../../../../common/components';
import * as handlers from '../../../../utils/handlers';
import {
  TRUST_AGREEMENT_COLLECTION_ITEM,
  TRUST_AGREEMENT_LOCATION_ITEM,
  TRUST_AGREEMENT_MEMORIAL_ITEM,
  TRUST_AGREEMENT_PROPERTY,
  TRUST_BENIFICIARY,
  TRUST_DESIGNATED_BENIFICIARY,
  TRUST_PURCHASER,
} from '../../../../utils/types';
import ItemEmptyTile from '../../../reports/item-empty-tile/item-empty-tile';
import { BeneficiartDetailsTable, ContractDetailsStatus, ContractDetailsTable } from '../index';

function ContractDetails({ agreementId, setDisabled, trustContractType, isCancelledContract }) {
  const [contractBasicData, setContractBasicData] = useState([]);
  const [contractTrustID, setContractTrustID] = useState('');
  const [contractData, setContractData] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const [agreementItem, setAgreementItem] = useState([]);
  const [isBeneficiariesDesignated, setIsBeneficiariesDesignated] = useState(false);

  useEffect(() => {
    if (agreementId) init();
  }, [agreementId]);

  /** Initialize Contract Basic Data */
  const init = async () => {
    try {
      setLoading(true);
      setDisabled(true);
      const { data: res } = await getTrustAccountDetail({ id: agreementId });
      setLoading(false);
      if (res) {
        const { data } = res || {};

        if (isCancelledContract) {
          data.agreementItems = [];
          data.sharedItems = [];
        }

        const {
          contractNumber,
          agreementPersons,
          agreementBenificiaryList,
          agreementItems,
          sharedItems,
          intermentRights,
          lastServiceConsumptionDate,
          paidInFull,
          paymentSchedule,
          serviceOwnershipType,
          submissionDate,
          validationDate,
        } = data || {};

        const allItems = Array.from([
          ...(agreementItems?.length ? agreementItems : []),
          ...(sharedItems?.length ? sharedItems : []),
        ]);

        const uniqueAgreementItems =
          allItems
            ?.filter(
              (ele, index) =>
                allItems?.findIndex(
                  (obj) =>
                    obj?.locationItemId === ele?.locationItemId &&
                    (ele?.resourceType === TRUST_AGREEMENT_LOCATION_ITEM ||
                      ele?.resourceType === TRUST_AGREEMENT_MEMORIAL_ITEM ||
                      ele?.resourceType === TRUST_AGREEMENT_COLLECTION_ITEM),
                ) === index,
            )
            ?.map((ele) => {
              // eslint-disable-next-line no-param-reassign
              ele.quantity =
                allItems?.filter(
                  (value) =>
                    ele?.locationItemId === value?.locationItemId &&
                    (ele?.resourceType === TRUST_AGREEMENT_LOCATION_ITEM ||
                      ele?.resourceType === TRUST_AGREEMENT_MEMORIAL_ITEM ||
                      ele?.resourceType === TRUST_AGREEMENT_COLLECTION_ITEM),
                )?.length || 0;
              return ele;
            }) || [];

        const uniquePropertyItems =
          allItems
            ?.filter(
              (ele, index) =>
                allItems?.findIndex(
                  (obj) =>
                    obj?.resourceId === ele?.resourceId &&
                    ele?.resourceType === TRUST_AGREEMENT_PROPERTY,
                ) === index,
            )
            ?.map((ele) => {
              // eslint-disable-next-line no-param-reassign
              ele.quantity =
                allItems?.filter(
                  (value) =>
                    ele?.resourceId === value?.resourceId &&
                    ele?.resourceType === TRUST_AGREEMENT_PROPERTY,
                )?.length || 0;
              return ele;
            }) || [];

        setContractTrustID(contractNumber);
        setAgreementItem([...uniqueAgreementItems, ...uniquePropertyItems]);

        // Basic contract data
        const purchaserDetail = agreementBenificiaryList?.length
          ? agreementBenificiaryList.filter((ele) => ele.role === TRUST_PURCHASER)[0]
          : null;
        const {
          onePortalId: purchaserOnePortalId,
          firstName = '',
          lastName = '',
        } = purchaserDetail || {};
        const purchaserName = `${firstName} ${lastName}`.trim();

        let beneficiaryDetail = agreementPersons?.length
          ? agreementPersons.filter((ele) => ele.role === TRUST_BENIFICIARY)
          : null;

        const beneficiaryDetailCount = beneficiaryDetail?.length || 0;

        if (beneficiaryDetailCount < intermentRights) {
          beneficiaryDetail = agreementPersons?.length
            ? agreementPersons.slice(0, intermentRights)
            : null;
        }

        let contractDetail = [
          {
            id: 1,
            key: 'Customer AC number / OPI',
            value: purchaserOnePortalId || 'NA',
          },
          { id: 2, key: 'Purchaser Name', value: purchaserName || 'NA' },
          { id: 3, key: 'Interment Rights', value: intermentRights },
          { id: 4, key: 'Merchandise and Services', value: 'VIEW' },
          {
            id: 5,
            key: 'Payment Schedule',
            value: `EMI for ${paymentSchedule || 0} Months`,
          },
          {
            id: 6,
            key: 'Last Service Consumption Date',
            value: formatDate(lastServiceConsumptionDate),
          },
          {
            id: 7,
            key: 'Contract Validation Date',
            value: formatDate(validationDate),
          },
          {
            id: 8,
            key: 'Contract Submission Date',
            value: formatDate(submissionDate),
          },
          {
            id: 9,
            key: 'Survivor Ownership Type',
            value: serviceOwnershipType || 'NA',
          },
        ];

        if (agreementPersons?.length) {
          setIsBeneficiariesDesignated(
            agreementPersons.findIndex((ele) => ele.role === TRUST_PURCHASER) === -1,
          );

          contractDetail = [
            ...contractDetail,
            ...agreementPersons.map(
              // eslint-disable-next-line no-shadow
              ({ firstName = '', lastName = '', onePortalId = '' }, index) => {
                const name = `${firstName} ${lastName}`.trim();

                return {
                  id: index + 10,
                  key: `${TRUST_DESIGNATED_BENIFICIARY} ${index + 1}`,
                  value: `${name} (${onePortalId})`,
                };
              },
            ),
          ];
        }

        setContractBasicData(Array.from(contractDetail));

        // Contract projection data;
        const obj = {
          agreementPersons: [],
          contractNumbers: [],
          items: [],
          sharedServices: [],
          paidInFull,
        };

        if (agreementPersons?.length) {
          agreementPersons.forEach((ele) => {
            const {
              // eslint-disable-next-line no-shadow
              firstName = '',
              // eslint-disable-next-line no-shadow
              lastName = '',
              trustAccountNumber = '',
              onePortalId = '',
            } = ele || {};

            obj.agreementPersons.push({
              label: `${firstName} ${lastName}`.trim(),
              opi: onePortalId,
            });

            obj.contractNumbers.push(trustAccountNumber);
          });

          if (agreementItems?.length) {
            agreementItems.forEach((ele) => {
              const { itemName, status, code, trustAccountId } = ele;
              let row = [
                {
                  label: itemName || '',
                  code,
                },
              ];

              row = [...row, ...Array(agreementPersons.length).fill('')];

              const index = agreementPersons.findIndex(
                (value) => value.trustAccountId === trustAccountId,
              );
              if (index !== -1) {
                row[index + 1] = {
                  label: status || '',
                };
              }

              obj.items.push(row);
            });
          }

          if (sharedItems?.length) {
            sharedItems.forEach((ele) => {
              const { itemName = '', status, code, trustAccountId } = ele;
              let row = [
                {
                  label: itemName,
                  code,
                },
              ];

              row = [...row, ...Array(agreementPersons.length).fill('')];

              const index = agreementPersons.findIndex(
                (value) => value.trustAccountId === trustAccountId,
              );
              if (index !== -1) {
                row[index + 1] = {
                  label: status,
                };
              }

              obj.sharedServices.push(row);
            });
          }

          setContractData(obj);
        } else setContractData(obj);
      }
    } catch (error) {
      setLoading(false);
      handlers.apiErrorHandler(error);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'NA';

    return moment(date).format('DD-MMM-YY');
  };

  return (
    <Card className="detailed-view">
      <CardHeader>
        <CardTitle>Contract Details: {contractTrustID}</CardTitle>
        <CardSubtitle>Trust Contract</CardSubtitle>
      </CardHeader>
      <CardBody>
        {contractBasicData?.length ? (
          <ContractDetailsTable
            data={contractBasicData}
            services={agreementItem}
            isCancelledContract={isCancelledContract}
          />
        ) : (
          <ItemEmptyTile />
        )}
        {!loading ? (
          <>
            <ContractDetailsStatus
              paidFull={contractData?.paidInFull}
              // eslint-disable-next-line camelcase
              beneficiariesDesignated={isBeneficiariesDesignated}
              contractNumber={contractTrustID}
            />
            {contractData?.agreementPersons?.length ? (
              <BeneficiartDetailsTable
                contractData={contractData}
                agreementId={agreementId}
                trustContractType={trustContractType}
              />
            ) : null}
          </>
        ) : (
          <Loader />
        )}
      </CardBody>
    </Card>
  );
}

ContractDetails.propTypes = {
  agreementId: PropTypes.number,
  trustContractType: PropTypes.string,
  setDisabled: PropTypes.func,
  isCancelledContract: PropTypes.bool,
};

export default ContractDetails;
